const TARGET_W = 1280;
const TARGET_H = 960;
const JPEG_QUALITY = 0.82;

/**
 * Replicates the backend ImageHelper transformation in the browser:
 *   - scales to cover 1280×960 (4:3)
 *   - center-crops to exactly 1280×960
 *   - encodes as JPEG at quality 0.82
 *
 * Accepts any image the browser can decode (JPEG, PNG, GIF, WebP, …) at any
 * file size. The result is always a predictably-sized JPEG well under 10 MB,
 * so no client-side size filtering is needed.
 *
 * @param {File} file - original image file
 * @returns {Promise<File>} processed JPEG File with the same base name
 */
export const processImageForUpload = (file) =>
    new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            const sw = img.naturalWidth;
            const sh = img.naturalHeight;

            // Scale to cover the target rectangle, then crop from center
            // (equivalent to Thumbnailator .size(W,H).crop(Positions.CENTER))
            const scale = Math.max(TARGET_W / sw, TARGET_H / sh);
            const srcW = TARGET_W / scale;
            const srcH = TARGET_H / scale;
            const srcX = (sw - srcW) / 2;
            const srcY = (sh - srcH) / 2;

            const canvas = document.createElement("canvas");
            canvas.width = TARGET_W;
            canvas.height = TARGET_H;
            canvas.getContext("2d").drawImage(img, srcX, srcY, srcW, srcH, 0, 0, TARGET_W, TARGET_H);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error(`Failed to encode image: ${file.name}`));
                        return;
                    }
                    const outName = file.name.replace(/\.[^.]+$/, ".jpg");
                    resolve(new File([blob], outName, { type: "image/jpeg" }));
                },
                "image/jpeg",
                JPEG_QUALITY
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error(`Failed to load image: ${file.name}`));
        };

        img.src = objectUrl;
    });
